/**
 * App.jsx — Main Application Router
 * Kitchly v0.5.5
 *
 * v0.5.5: Added /admin route.
 */
import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from './context/AuthContext';
import BottomNav from './components/layout/BottomNav';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import DashboardPage from './pages/DashboardPage';
import InventoryPage from './pages/InventoryPage';
import ShoppingPage from './pages/ShoppingPage';
import ScanPage from './pages/ScanPage';
import FamilyPage from './pages/FamilyPage';
import SettingsPage from './pages/SettingsPage';
import PutAwayPage from './pages/PutAwayPage';
import AddItemPage from './pages/AddItemPage';
import FeedbackPage from './pages/FeedbackPage';
import AdminPage from './pages/AdminPage';

/**
 * Protected route wrapper
 */
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="page-loader">
        <div className="loader-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

/**
 * Public route wrapper — redirects to dashboard if already logged in
 */
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="page-loader">
        <div className="loader-spinner" />
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Pages where bottom nav should be hidden
const HIDE_NAV_PATHS = ['/login', '/register', '/forgot-password', '/scan', '/put-away', '/add-item', '/feedback', '/admin'];

function App() {
  const location = useLocation();
  const showNav = !HIDE_NAV_PATHS.some(p => location.pathname.startsWith(p));

  return (
    <div className="app">
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#3D3D3D',
            color: '#FAF8F5',
            borderRadius: '12px',
            padding: '12px 16px',
            fontSize: '14px',
            marginBottom: showNav ? '80px' : '16px'
          },
          success: { iconTheme: { primary: '#7A9E7E', secondary: '#FAF8F5' } },
          error: { iconTheme: { primary: '#D9534F', secondary: '#FAF8F5' } }
        }}
      />

      <main className={`main-content ${showNav ? 'with-nav' : ''}`}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<PublicRoute><ForgotPasswordPage /></PublicRoute>} />

          {/* Protected routes */}
          <Route path="/" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
          <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
          <Route path="/shopping" element={<ProtectedRoute><ShoppingPage /></ProtectedRoute>} />
          <Route path="/scan" element={<ProtectedRoute><ScanPage /></ProtectedRoute>} />
          <Route path="/family" element={<ProtectedRoute><FamilyPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/put-away" element={<ProtectedRoute><PutAwayPage /></ProtectedRoute>} />
          <Route path="/add-item" element={<ProtectedRoute><AddItemPage /></ProtectedRoute>} />
          <Route path="/feedback" element={<ProtectedRoute><FeedbackPage /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      {showNav && <BottomNav />}
    </div>
  );
}

export default App;
